import { Link, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { NEMA_LOGO } from "@/assets/logo";
import { TID } from "@/constants/testIds";

const links = [
  { label: "Live TV",   to: "/#regions", tid: TID.nav.linkChannels },
  { label: "Cloud DVR", to: "/#dvr",     tid: "nav-link-dvr" },
  { label: "Devices",   to: "/#devices", tid: "nav-link-devices" },
];

const packages = [
  { label: "Desi TV",   to: "/packages/desi-tv",   tag: "Live",        tid: "nav-package-desi" },
  { label: "Arabic TV", to: "/packages/arabic-tv", tag: "Coming soon", tid: "nav-package-arabic" },
];

const PackagesMenu = ({ mobile = false, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  if (mobile) {
    return (
      <div className="flex flex-col gap-1">
        <button
          onClick={() => setOpen((s) => !s)}
          data-testid="nav-link-packages-mobile"
          className="flex items-center justify-between text-white/80 text-base py-1"
        >
          Packages <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="pl-3 flex flex-col gap-2 py-1">
            {packages.map((p) => (
              <Link
                key={p.label}
                to={p.to}
                data-testid={`${p.tid}-mobile`}
                onClick={onNavigate}
                className="flex items-center justify-between text-sm text-white/75 py-1"
              >
                {p.label}
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{p.tag}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((s) => !s)}
        onMouseEnter={() => setOpen(true)}
        data-testid="nav-link-packages"
        className="inline-flex items-center gap-1 text-sm font-medium text-white/70 hover:text-white transition-colors"
      >
        Packages <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          onMouseLeave={() => setOpen(false)}
          data-testid="nav-packages-menu"
          className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-64 z-50"
        >
          <div className="rounded-2xl border border-white/12 bg-[#0F0A19]/95 backdrop-blur-xl p-2 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
            {packages.map((p) => (
              <Link
                key={p.label}
                to={p.to}
                data-testid={p.tid}
                onClick={() => setOpen(false)}
                className="flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 hover:bg-white/5 transition-colors"
              >
                <span className="font-semibold text-white text-sm">{p.label}</span>
                <span className={`text-[9px] uppercase tracking-[0.2em] rounded-full px-2 py-0.5 border ${p.tag === "Live" ? "text-red-300 border-red-400/40 bg-red-500/10" : "text-[#67e8f9] border-[#22D3EE]/40 bg-[#22D3EE]/10"}`}>
                  {p.tag}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const { scrollY } = useScroll();
  const bg = useTransform(scrollY, [0, 80], ["rgba(5,3,10,0)", "rgba(5,3,10,0.75)"]);
  const border = useTransform(scrollY, [0, 80], ["rgba(255,255,255,0)", "rgba(255,255,255,0.08)"]);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const goToPackages = () => {
    if (loc.pathname === "/") {
      document.getElementById("top")?.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = "/#top";
    }
  };

  return (
    <motion.header
      data-testid={TID.nav.root}
      style={{ backgroundColor: bg, borderColor: border }}
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl border-b"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between gap-6">
        <Link to="/" data-testid={TID.nav.logo} className="flex items-center gap-3 shrink-0">
          <img src={NEMA_LOGO} alt="Nema TV" className="h-9 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          <Link to={links[0].to} data-testid={links[0].tid} className="text-sm font-medium text-white/70 hover:text-white transition-colors">
            {links[0].label}
          </Link>
          <PackagesMenu />
          {links.slice(1).map((l) => (
            <Link key={l.label} to={l.to} data-testid={l.tid} className="text-sm font-medium text-white/70 hover:text-white transition-colors">
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {loc.pathname.startsWith("/packages/desi-tv") ? (
            <>
              <a
                href="https://tv.nematv.com/auth/sign-in"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-cta-signin"
                className="hidden sm:inline-flex text-sm font-medium text-white/80 hover:text-white transition-colors px-3 py-2.5"
              >
                Sign in
              </a>
              <a
                href="https://tv.nematv.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid={TID.nav.ctaSubscribe}
                className="hidden sm:inline-flex rounded-full text-[#0a0503] px-5 py-2.5 text-sm font-bold hover:scale-[1.02] transition-transform"
                style={{
                  background: "linear-gradient(90deg, #FFB800 0%, #FF7A1A 50%, #F43F5E 100%)",
                  boxShadow: "0 0 25px rgba(255,138,0,0.35)",
                }}
              >
                Start Watching
              </a>
            </>
          ) : (
            <button
              onClick={goToPackages}
              data-testid={TID.nav.ctaSubscribe}
              className="hidden sm:inline-flex rounded-full bg-gradient-to-r from-[#6B21D9] to-[#22D3EE] text-white px-5 py-2.5 text-sm font-semibold hover:shadow-[0_0_25px_rgba(34,211,238,0.35)] transition-shadow"
            >
              Choose your package
            </button>
          )}
          <button
            className="lg:hidden text-white/80"
            onClick={() => setOpen((s) => !s)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#05030A]/95 border-t border-white/10 px-6 py-4 flex flex-col gap-3">
          <Link to={links[0].to} data-testid={`${links[0].tid}-mobile`} className="text-white/80 text-base py-1">{links[0].label}</Link>
          <PackagesMenu mobile onNavigate={() => setOpen(false)} />
          {links.slice(1).map((l) => (
            <Link key={l.label} to={l.to} data-testid={`${l.tid}-mobile`} className="text-white/80 text-base py-1">{l.label}</Link>
          ))}
          {loc.pathname.startsWith("/packages/desi-tv") ? (
            <div className="mt-2 flex flex-col gap-2">
              <a
                href="https://tv.nematv.com/auth/sign-in"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="nav-cta-signin-mobile"
                className="rounded-full border border-white/20 text-white px-5 py-2.5 text-sm font-semibold text-center"
              >
                Sign in
              </a>
              <a
                href="https://tv.nematv.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="nav-cta-subscribe-mobile"
                className="rounded-full text-[#0a0503] px-5 py-2.5 text-sm font-bold text-center"
                style={{ background: "linear-gradient(90deg, #FFB800 0%, #FF7A1A 50%, #F43F5E 100%)" }}
              >
                Start Watching
              </a>
            </div>
          ) : (
            <button
              onClick={() => { setOpen(false); goToPackages(); }}
              data-testid="nav-cta-subscribe-mobile"
              className="mt-2 rounded-full bg-gradient-to-r from-[#6B21D9] to-[#22D3EE] text-white px-5 py-2.5 text-sm font-semibold text-center"
            >
              Choose your package
            </button>
          )}
        </div>
      )}
    </motion.header>
  );
};

export default Navbar;
